import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { api, apiFetch } from "../../api/client";
import { keys, useProjects } from "../../api/hooks";
import type { ProjectSummary } from "../../api/types";
import { useSession } from "../../app/store";
import { Modal, errText } from "../../components/ui";

type Mode = "blank" | "copy" | "legacy" | "import";

export function CreateProjectDialog({ onClose }: { onClose: () => void }) {
  const projects = useProjects();
  const qc = useQueryClient();
  const setProject = useSession((s) => s.setProject);
  const toast = useSession((s) => s.toast);
  const [mode, setMode] = useState<Mode>("copy");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [copyFrom, setCopyFrom] = useState("happyair_standard");
  const [legacy, setLegacy] = useState<{ prm?: File; alg?: File; prt?: File; to?: File; cr?: File; output: string }>({
    output: "SAGE_FORMAT",
  });
  const [archive, setArchive] = useState<File | null>(null);

  const create = useMutation({
    mutationFn: async (): Promise<ProjectSummary> => {
      if (mode === "blank" || mode === "copy") {
        return api.post<ProjectSummary>("/projects", {
          name,
          description,
          copy_from: mode === "copy" ? copyFrom : null,
        });
      }
      if (mode === "legacy") {
        const form = new FormData();
        form.append("name", name);
        form.append("output_type", legacy.output);
        if (legacy.prm) form.append("prm", legacy.prm);
        if (legacy.alg) form.append("algorithm_csv", legacy.alg);
        if (legacy.prt) form.append("print_csv", legacy.prt);
        if (legacy.to) form.append("takeoff_template", legacy.to);
        if (legacy.cr) form.append("cruise_template", legacy.cr);
        return apiFetch<ProjectSummary>("/projects/import-legacy", { method: "POST", body: form });
      }
      const form = new FormData();
      if (archive) form.append("archive", archive);
      if (name) form.append("name", name);
      return apiFetch<ProjectSummary>("/projects/import", { method: "POST", body: form });
    },
    onSuccess: async (p) => {
      // The shell falls back to the first project whenever the selected id is not in the list,
      // so the list must contain the new project before it is selected.
      await qc.invalidateQueries({ queryKey: keys.projects });
      setProject(p.id);
      toast(`Project "${p.name}" created`, "success");
      onClose();
    },
    onError: (e) => toast(errText(e), "error"),
  });

  const canSubmit =
    (mode === "import" ? !!archive : !!name.trim()) &&
    (mode !== "legacy" || (!!legacy.prm && !!legacy.alg && !!legacy.prt));

  return (
    <Modal
      title="New project"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button className="btn primary" disabled={!canSubmit || create.isPending} onClick={() => create.mutate()}>
            {create.isPending ? "Creating…" : "Create"}
          </button>
        </>
      }
    >
      <div className="tabs" style={{ margin: "-14px -16px 6px", padding: "0 8px" }}>
        {(
          [
            ["copy", "From example"],
            ["blank", "Blank"],
            ["legacy", "Import ACMS CSV + templates"],
            ["import", "Import project zip"],
          ] as [Mode, string][]
        ).map(([m, label]) => (
          <button key={m} className={mode === m ? "on" : ""} onClick={() => setMode(m)}>
            {label}
          </button>
        ))}
      </div>
      {mode !== "import" ? (
        <label className="field">
          <span>Name</span>
          <input className="inp" value={name} onChange={(e) => setName(e.target.value)} autoFocus placeholder="e.g. B737 fleet reports" />
        </label>
      ) : null}
      {mode === "blank" || mode === "copy" ? (
        <label className="field">
          <span>Description</span>
          <input className="inp" value={description} onChange={(e) => setDescription(e.target.value)} />
        </label>
      ) : null}
      {mode === "copy" ? (
        <label className="field">
          <span>Copy from</span>
          <select className="inp" value={copyFrom} onChange={(e) => setCopyFrom(e.target.value)}>
            {(projects.data ?? []).map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </label>
      ) : null}
      {mode === "legacy" ? (
        <>
          <p className="dim small" style={{ margin: 0 }}>
            Converts an existing ACMS "standard" configuration into a project: the PRM becomes the frame, the algorithm CSV
            becomes <code>inputs/algorithm.yaml</code>, the print CSV becomes report value lists and the templates are
            kept unchanged as bracket-template outputs.
          </p>
          <FilePick label="PRM file" onFile={(f) => setLegacy({ ...legacy, prm: f })} file={legacy.prm} />
          <FilePick label="Algorithm parameters CSV" onFile={(f) => setLegacy({ ...legacy, alg: f })} file={legacy.alg} />
          <FilePick label="Print report parameters CSV" onFile={(f) => setLegacy({ ...legacy, prt: f })} file={legacy.prt} />
          <FilePick label="Take-off template (optional)" onFile={(f) => setLegacy({ ...legacy, to: f })} file={legacy.to} />
          <FilePick label="Cruise template (optional)" onFile={(f) => setLegacy({ ...legacy, cr: f })} file={legacy.cr} />
          <label className="field">
            <span>Output type</span>
            <select className="inp" value={legacy.output} onChange={(e) => setLegacy({ ...legacy, output: e.target.value })}>
              <option>SAGE_FORMAT</option>
              <option>DIG</option>
              <option>TXT</option>
            </select>
          </label>
        </>
      ) : null}
      {mode === "import" ? (
        <>
          <FilePick label="Project zip (exported from Reports Studio)" onFile={(f) => setArchive(f)} file={archive ?? undefined} />
          <label className="field">
            <span>Rename to (optional)</span>
            <input className="inp" value={name} onChange={(e) => setName(e.target.value)} />
          </label>
        </>
      ) : null}
    </Modal>
  );
}

function FilePick({ label, file, onFile }: { label: string; file?: File; onFile: (f: File) => void }) {
  return (
    <label className="field">
      <span>{label}</span>
      <div className="row">
        <input type="file" onChange={(e) => e.target.files?.[0] && onFile(e.target.files[0])} />
        {file ? <span className="badge ok">{file.name}</span> : null}
      </div>
    </label>
  );
}
